import { getFunctions, httpsCallable } from "firebase/functions";
import { Capacitor } from "@capacitor/core";
import { app } from "../firebaseConfig";

export type ClientSyncErrorPayload = {
  event: string;
  reason?: string;
  errorCode?: string;
  errorMessage?: string;
  itemId?: string;
  queueItemId?: string;
  orderId?: string;
  shipmentId?: string;
  updateMethod?: string;
  context?: string;
  queueSize?: number;
  queuePaused?: boolean;
  pauseReason?: string;
  isOnline?: boolean;
  isForcedOffline?: boolean;
  platform?: string;
  sessionId?: string;
  userAgent?: string;
  appVersion?: string;
  timestamp?: number;
  uid?: string;
  email?: string;
  role?: string;
};

type ClientSyncErrorResponse = { ok: boolean };

const functions = getFunctions(app, "us-central1");
const logFn = httpsCallable<ClientSyncErrorPayload, ClientSyncErrorResponse>(
  functions,
  "logClientSyncError"
);

const SESSION_KEY = "sync-error-session-id";
const LOG_THROTTLE_MS = 15000;
const lastLogByKey = new Map<string, number>();
let fallbackSessionId: string | null = null;

const getSessionId = (): string => {
  if (fallbackSessionId) return fallbackSessionId;
  try {
    if (typeof sessionStorage !== "undefined") {
      const existing = sessionStorage.getItem(SESSION_KEY);
      if (existing) {
        fallbackSessionId = existing;
        return existing;
      }
      const created = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, created);
      fallbackSessionId = created;
      return created;
    }
  } catch {
    // ignore storage failures
  }
  fallbackSessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return fallbackSessionId;
};

const buildKey = (payload: ClientSyncErrorPayload): string =>
  `${payload.event}|${payload.reason ?? ""}|${payload.errorCode ?? ""}|${payload.itemId ?? ""}`;

export const logClientSyncError = async (
  payload: ClientSyncErrorPayload
): Promise<void> => {
  const now = Date.now();
  const key = buildKey(payload);
  const last = lastLogByKey.get(key);
  if (typeof last === "number" && now - last < LOG_THROTTLE_MS) {
    return;
  }
  lastLogByKey.set(key, now);

  const platform = payload.platform ?? (Capacitor.getPlatform?.() ?? "web");
  const sessionId = payload.sessionId ?? getSessionId();
  const userAgent =
    typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 256) : undefined;

  try {
    await logFn({
      ...payload,
      platform,
      sessionId,
      userAgent,
      timestamp: payload.timestamp ?? now,
    });
  } catch (error) {
    console.warn("[logClientSyncError] failed to log sync error", error);
  }
};
